// Cursive font path extraction.
//
// Loads the Cedarville Cursive TTF (Google Fonts, teacher-designed for
// American cursive) and converts any string into an SVG path skeleton that
// matches the exact glyph the user sees. This keeps the tracing pen tip and
// the visible letter in the SAME coordinate space — the previous approach
// used hand-coded paths for tracing while rendering the letter with a font,
// which caused the "Show me" pen to draw NEXT to the letter instead of on it.
//
// The font is fetched once, cached in memory, and exposes a React hook so
// components re-render when it's ready.

import opentype from "opentype.js";

const FONT_URL =
  "https://fonts.gstatic.com/s/cedarvillecursive/v18/yYL00g_a2veiudhUmxjo5VKkoqA-B_nuIrp1.ttf";

let cachedFont = null;
let loadingPromise = null;
const listeners = new Set();

async function loadFont() {
  if (cachedFont) return cachedFont;
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    try {
      const buf = await fetch(FONT_URL).then((r) => r.arrayBuffer());
      cachedFont = opentype.parse(buf);
      listeners.forEach((cb) => cb(cachedFont));
      return cachedFont;
    } catch (e) {
      // Any failure just leaves cachedFont null — callers fall back to the
      // hand-coded SVG paths. Log so it shows up in dev tools if it ever
      // breaks in production.
      // eslint-disable-next-line no-console
      console.warn("[cursiveFont] failed to load Cedarville Cursive:", e);
      loadingPromise = null;
      return null;
    }
  })();
  return loadingPromise;
}

export function getCursiveFont() {
  if (!cachedFont && !loadingPromise) loadFont();
  return cachedFont;
}

// React hook — returns the font once loaded and forces a re-render then.
import { useEffect, useState } from "react";
export function useCursiveFont() {
  const [font, setFont] = useState(cachedFont);
  useEffect(() => {
    if (cachedFont) { setFont(cachedFont); return; }
    const cb = (f) => setFont(f);
    listeners.add(cb);
    loadFont();
    return () => listeners.delete(cb);
  }, []);
  return font;
}

// Natural handwriting start hints for uppercase cursive letters (Zaner-Bloser
// style). Font glyph outlines start wherever the type designer placed the
// first move-to (usually a corner or extreme point) — for handwriting we want
// the pen to begin where a teacher would draw first. Coords: `xr` is the
// fractional x-position (0 = leftmost outline point, 1 = rightmost) and `y`
// is engine y (baseline = 78, ascender top = 16).
const UPPER_START_HINT = {
  A: { xr: 0.90, y: 40 },  // top-right of oval belly
  B: { xr: 0.15, y: 24 },  // top of loop stem
  C: { xr: 0.95, y: 32 },  // top-right entry
  D: { xr: 0.15, y: 24 },  // top of loop stem
  E: { xr: 0.90, y: 32 },  // top-right of upper belly
  F: { xr: 0.55, y: 24 },  // top of loop stem
  G: { xr: 0.90, y: 34 },  // top-right entry
  H: { xr: 0.10, y: 24 },  // top of left loop stem
  I: { xr: 0.75, y: 26 },  // top-right of upper loop
  J: { xr: 0.80, y: 26 },  // top-right of upper loop
  K: { xr: 0.15, y: 24 },  // top of left loop stem
  L: { xr: 0.60, y: 30 },  // top of tall loop
  M: { xr: 0.10, y: 60 },  // low-left undercurve entry
  N: { xr: 0.10, y: 60 },  // low-left undercurve entry
  O: { xr: 0.90, y: 34 },  // top-right entry loop
  P: { xr: 0.45, y: 26 },  // top of stem (descends into desc zone)
  Q: { xr: 0.15, y: 32 },  // top-left entry
  R: { xr: 0.15, y: 24 },  // top of loop stem
  S: { xr: 0.15, y: 32 },  // top-left entry
  T: { xr: 0.55, y: 24 },  // top of loop stem
  U: { xr: 0.15, y: 26 },  // top-left of first curl
  V: { xr: 0.10, y: 30 },  // top-left
  W: { xr: 0.05, y: 28 },  // top-left
  X: { xr: 0.15, y: 28 },  // top-left (first diagonal)
  Y: { xr: 0.10, y: 30 },  // top-left
  Z: { xr: 0.15, y: 30 },  // top-left of top curve
};

// Rotate a closed SVG path so its drawing sequence starts near (targetX,
// targetY). We sample the path densely via the browser's SVG engine, find the
// closest sample to the target, then rebuild the path as a polyline that
// begins there and wraps around back to the start. This preserves the shape
// (fill still renders identically) while giving the animator + start-dot the
// correct handwriting entry point. Cached per (d) so repeat calls are cheap.
const rotateCache = new Map();
function rotatePathToStart(d, targetX, targetY, sampleCount = 400) {
  if (typeof document === "undefined") return d;
  const key = `${d}|${targetX.toFixed(1)}|${targetY.toFixed(1)}`;
  if (rotateCache.has(key)) return rotateCache.get(key);
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "0");
  svg.setAttribute("height", "0");
  svg.style.position = "absolute";
  svg.style.visibility = "hidden";
  const p = document.createElementNS(NS, "path");
  p.setAttribute("d", d);
  svg.appendChild(p);
  document.body.appendChild(svg);
  let total = 0;
  try { total = p.getTotalLength(); } catch (e) { /* ignore */ }
  if (!total) {
    document.body.removeChild(svg);
    rotateCache.set(key, d);
    return d;
  }
  const points = new Array(sampleCount);
  let bestI = 0;
  let bestDist = Infinity;
  for (let i = 0; i < sampleCount; i++) {
    const pt = p.getPointAtLength((i / sampleCount) * total);
    points[i] = { x: pt.x, y: pt.y };
    const dx = pt.x - targetX;
    const dy = pt.y - targetY;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) { bestDist = dist; bestI = i; }
  }
  document.body.removeChild(svg);
  const out = ["M", points[bestI].x.toFixed(2), points[bestI].y.toFixed(2)];
  for (let k = 1; k < sampleCount; k++) {
    const pt = points[(bestI + k) % sampleCount];
    out.push("L", pt.x.toFixed(2), pt.y.toFixed(2));
  }
  out.push("Z");
  const rotated = out.join(" ");
  rotateCache.set(key, rotated);
  return rotated;
}

// Get the bounding box of an SVG path via the browser DOM. Used to translate
// per-letter start hints (expressed as fractions of glyph width) into
// absolute coordinates on the rendered font outline.
function getPathBBox(d) {
  if (typeof document === "undefined") return null;
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "0");
  svg.setAttribute("height", "0");
  svg.style.position = "absolute";
  svg.style.visibility = "hidden";
  const p = document.createElementNS(NS, "path");
  p.setAttribute("d", d);
  svg.appendChild(p);
  document.body.appendChild(svg);
  let bbox = null;
  try { bbox = p.getBBox(); } catch (e) { /* ignore */ }
  document.body.removeChild(svg);
  return bbox;
}

// Convert a font glyph's outline into an SVG path in the 0..100 coordinate
// system that the tracing engine uses. The font metric is normalised so the
// glyph sits on the baseline with ascender/x-height/descender aligned to the
// engine's guide lines.
export function glyphPathsForText(font, text, opts = {}) {
  if (!font || !text) return null;
  const {
    baseline = 78,         // engine baseline
    ascenderTop = 16,      // engine ascender top
    padLeft = 6,           // left margin
  } = opts;

  // Cedarville Cursive metrics
  const unitsPerEm = font.unitsPerEm;
  const asc = font.ascender;            // in font units
  const emHeightPx = baseline - ascenderTop; // 62 units in engine space
  // Font size such that (ascender - descender) fits inside emHeightPx.
  // Cedarville has a tall internal x-height + long descenders; empirically
  // 1.35× gives the right visual weight.
  const fontSize = (emHeightPx / (asc / unitsPerEm)) * 1.35;

  // Advance the pen across characters using the font's own kerning + hmtx.
  const strokes = [];
  let penX = padLeft;
  const chars = Array.from(text);
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    if (ch === " ") { penX += fontSize * 0.4; continue; }
    const glyph = font.charToGlyph(ch);
    if (!glyph) continue;
    // opentype gives us a Path in font units at (0, 0) baseline. Scale to
    // engine px and translate so baseline sits at `baseline`.
    const path = glyph.getPath(penX, baseline, fontSize);
    const d = path.toPathData(2);
    if (d && d.trim()) {
      // Split on M commands so multi-contour glyphs (like i, j with dots)
      // become separate strokes for the animator.
      const parts = splitByM(d);
      // For uppercase letters, rotate the primary (largest) contour so the
      // animator + start dot begin at the natural handwriting entry point.
      const hint = UPPER_START_HINT[ch];
      if (hint && parts.length) {
        // Find the largest contour by bbox area — that's the letter body
        // (the smaller contours are dots / i-tittles / crossbars).
        let primaryIdx = 0;
        let primaryArea = 0;
        for (let j = 0; j < parts.length; j++) {
          const bb = getPathBBox(parts[j]);
          const area = bb ? bb.width * bb.height : 0;
          if (area > primaryArea) { primaryArea = area; primaryIdx = j; }
        }
        const primaryBBox = getPathBBox(parts[primaryIdx]);
        if (primaryBBox) {
          const targetX = primaryBBox.x + hint.xr * primaryBBox.width;
          parts[primaryIdx] = rotatePathToStart(parts[primaryIdx], targetX, hint.y);
        }
      }
      for (const sub of parts) {
        strokes.push({ d: sub, start: firstMoveTo(sub) });
      }
    }
    // Advance pen using glyph's own advance width + kerning to the next char.
    const scale = fontSize / unitsPerEm;
    penX += glyph.advanceWidth * scale;
    if (i + 1 < chars.length) {
      const next = font.charToGlyph(chars[i + 1]);
      if (next) {
        const kern = font.getKerningValue(glyph, next) || 0;
        penX += kern * scale;
      }
    }
  }
  if (!strokes.length) return null;
  const width = Math.max(80, penX + padLeft);
  return {
    viewBox: `0 0 ${width} 100`,
    width,
    strokes,
    startingPoint: strokes[0].start,
    glyphText: text,
  };
}

function splitByM(d) {
  const parts = [];
  let start = 0;
  for (let i = 1; i < d.length; i++) {
    if (d[i] === "M" || d[i] === "m") {
      parts.push(d.slice(start, i).trim());
      start = i;
    }
  }
  parts.push(d.slice(start).trim());
  return parts.filter(Boolean);
}

function firstMoveTo(d) {
  const m = /M\s*(-?\d+\.?\d*)\s*[,\s]\s*(-?\d+\.?\d*)/.exec(d);
  return m ? { x: parseFloat(m[1]), y: parseFloat(m[2]) } : { x: 0, y: 0 };
}
